import {
  AddQuizFormVariantBody,
  CreateQuizBody,
  DeleteQuizBody,
  DuplicateQuizBody,
  PublishQuizBody,
  RemoveQuizFormVariantBody,
  UpdateQuizBody,
  UpdateQuizFormWeightsBody,
} from '@superfunnel/schemas';
import { DynamicPage, Quiz, QuizForm } from '@superfunnel/types';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';

// ============================================================================
// Quiz Collection References
// ============================================================================

export const quizzesColRef = (accountId: string) => {
  return collection(db, 'accounts', accountId, 'quizzes').withConverter(
    emptyConverter<Quiz>(),
  );
};

export const quizzesQuery = (accountId: string) => {
  return query(
    quizzesColRef(accountId),
    where('status', 'in', ['active', 'draft']),
  );
};

export const quizRef = (accountId: string, quizId: string) => {
  return doc(quizzesColRef(accountId), quizId);
};

/**
 * Mark a Quiz and its linked DynamicPage as having changes to publish.
 * Called when form data is modified.
 */
export const markQuizHasChangesToPublish = async (
  accountId: string,
  quizId: string,
): Promise<void> => {
  // Update the quiz
  const quizDocRef = quizRef(accountId, quizId);
  await setDoc(quizDocRef, { hasChangesToPublish: true }, { merge: true });

  // Get the linked DynamicPage ID from the quiz
  const quizDoc = await getDoc(quizDocRef);
  const quiz = quizDoc.data();

  if (quiz?.linkedDynamicPageId) {
    // Update the DynamicPage
    const dynamicPageRef = doc(
      db,
      'accounts',
      accountId,
      'campaigns',
      quiz.linkedDynamicPageId,
    ).withConverter(emptyConverter<DynamicPage>());

    await setDoc(
      dynamicPageRef,
      { hasChangesToPublish: true },
      { merge: true },
    );
  }
};

// ============================================================================
// Quiz Forms Collection References
// ============================================================================

export const quizFormsColRef = (accountId: string, quizId: string) => {
  return collection(
    db,
    'accounts',
    accountId,
    'quizzes',
    quizId,
    'forms',
  ).withConverter(emptyConverter<QuizForm>());
};

export const quizFormsQuery = (accountId: string, quizId: string) => {
  return query(
    quizFormsColRef(accountId, quizId),
    where('status', 'in', ['active', 'draft']),
  );
};

export const quizFormRef = (
  accountId: string,
  quizId: string,
  formId: string,
) => {
  return doc(quizFormsColRef(accountId, quizId), formId);
};

// ============================================================================
// Fetch All Quiz Forms for Account
// ============================================================================

export interface QuizFormWithContext {
  quizId: string;
  quizName: string;
  formId: string;
  form: QuizForm;
}

/**
 * Fetches all quiz forms across all quizzes in an account.
 * Since we can't use collection group queries without accountId in docs,
 * we fetch all quizzes first then fetch forms for each.
 */
export const getAllQuizFormsForAccount = async (
  accountId: string,
): Promise<QuizFormWithContext[]> => {
  const quizzesSnapshot = await getDocs(quizzesQuery(accountId));

  if (quizzesSnapshot.empty) {
    return [];
  }

  const allForms: QuizFormWithContext[] = [];

  // Fetch forms for each quiz in parallel
  await Promise.all(
    quizzesSnapshot.docs.map(async (quizDoc) => {
      const quiz = quizDoc.data();
      const formsSnapshot = await getDocs(
        quizFormsQuery(accountId, quizDoc.id),
      );

      formsSnapshot.docs.forEach((formDoc) => {
        allForms.push({
          quizId: quizDoc.id,
          quizName: quiz.name,
          formId: formDoc.id,
          form: formDoc.data(),
        });
      });
    }),
  );

  return allForms;
};

// ============================================================================
// Quiz CRUD Operations (via HTTP endpoints)
// ============================================================================

export interface CreateQuizResponse {
  quizId: string;
  dynamicPageId: string;
  formId: string;
}

export const createQuiz = async (
  params: CreateQuizBody,
): Promise<CreateQuizResponse> => {
  const fn = httpsCallable<CreateQuizBody, CreateQuizResponse>(
    functions,
    'accounts/quizzes/createQuiz',
  );

  const res = await fn(params);

  return res.data;
};

export const updateQuiz = async (
  accountId: string,
  quizId: string,
  data: Omit<UpdateQuizBody, 'accountId' | 'quizId'>,
): Promise<void> => {
  const fn = httpsCallable<UpdateQuizBody, void>(
    functions,
    'accounts/quizzes/updateQuiz',
  );

  await fn({
    accountId,
    quizId,
    ...data,
  });
};

export const deleteQuiz = async (
  accountId: string,
  quizId: string,
): Promise<void> => {
  const fn = httpsCallable<DeleteQuizBody, void>(
    functions,
    'accounts/quizzes/deleteQuiz',
  );

  await fn({ accountId, quizId });
};

export interface DuplicateQuizResponse {
  quizId: string;
  dynamicPageId: string;
}

export const duplicateQuiz = async (
  accountId: string,
  quizId: string,
  name?: string,
  path?: string,
): Promise<DuplicateQuizResponse> => {
  const fn = httpsCallable<DuplicateQuizBody, DuplicateQuizResponse>(
    functions,
    'accounts/quizzes/duplicateQuiz',
  );

  const res = await fn({
    accountId,
    quizId,
    name,
    path,
  });

  return res.data;
};

export const publishQuiz = async (
  accountId: string,
  quizId: string,
  versionName?: string,
  versionDescription?: string,
): Promise<{ versionId: string }> => {
  const fn = httpsCallable<PublishQuizBody, { versionId: string }>(
    functions,
    'accounts/quizzes/publishQuiz',
  );

  const res = await fn({
    accountId,
    quizId,
    versionName,
    versionDescription,
  });

  return res.data;
};

// ============================================================================
// Quiz Form Variant Operations (A/B Testing)
// ============================================================================

export interface AddFormVariantResponse {
  formId: string;
}

export const addFormVariant = async (
  accountId: string,
  quizId: string,
  sourceFormId: string,
  name?: string,
): Promise<AddFormVariantResponse> => {
  const fn = httpsCallable<AddQuizFormVariantBody, AddFormVariantResponse>(
    functions,
    'accounts/quizzes/addFormVariant',
  );

  const res = await fn({
    accountId,
    quizId,
    sourceFormId,
    name,
  });

  return res.data;
};

export const removeFormVariant = async (
  accountId: string,
  quizId: string,
  formId: string,
): Promise<void> => {
  const fn = httpsCallable<RemoveQuizFormVariantBody, void>(
    functions,
    'accounts/quizzes/removeFormVariant',
  );

  await fn({ accountId, quizId, formId });
};

export const updateFormWeights = async (
  accountId: string,
  quizId: string,
  weights: Record<string, number>,
): Promise<void> => {
  const fn = httpsCallable<UpdateQuizFormWeightsBody, void>(
    functions,
    'accounts/quizzes/updateFormWeights',
  );

  await fn({ accountId, quizId, weights });
};
